import { API_BASE_URL } from "../../utils/apiConfig";
import type {
  AnalyticsOverviewResponse,
  AnalyticsFunnelResponse,
  AnalyticsOperatorsResponse,
  AnalyticsFollowUpsResponse,
  AnalyticsTasksResponse,
  AnalyticsQualityResponse,
  AnalyticsFilters,
  AnalyticsRevenueResponse,
  AnalyticsOrdersProductsResponse,
  AnalyticsPerformanceResponse,
  AnalyticsCallbacksReasonsResponse,
  AnalyticsOutcomesResponse,
  AnalyticsShippingResponse,
} from "../../types/analytics";

function authHeaders(): HeadersInit {
  const token = localStorage.getItem("authToken");
  return {
    "Content-Type": "application/json",
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

function queryString(f: AnalyticsFilters): string {
  const params = new URLSearchParams();
  if (f.from) params.set("from", f.from);
  if (f.to) params.set("to", f.to);
  if (f.task) params.set("task", f.task);
  if (f.operatorId) params.set("operatorId", f.operatorId);
  if (f.outcome) params.set("outcome", f.outcome);
  return params.toString();
}

export async function fetchAnalyticsOverview(
  filters: AnalyticsFilters
): Promise<AnalyticsOverviewResponse> {
  const qs = queryString(filters);
  const res = await fetch(`${API_BASE_URL}/api/analytics/overview?${qs}`, {
    headers: authHeaders(),
  });
  if (!res.ok) throw new Error(await res.text());
  const json = await res.json();
  if (!json.success) throw new Error(json.error || "Failed to load overview");
  return json.data;
}

export async function fetchAnalyticsFunnel(
  filters: AnalyticsFilters
): Promise<AnalyticsFunnelResponse> {
  const qs = queryString(filters);
  const res = await fetch(`${API_BASE_URL}/api/analytics/funnel?${qs}`, {
    headers: authHeaders(),
  });
  if (!res.ok) throw new Error(await res.text());
  const json = await res.json();
  if (!json.success) throw new Error(json.error || "Failed to load funnel");
  return json.data;
}

export async function fetchAnalyticsOperators(
  filters: AnalyticsFilters,
  page = 1,
  limit = 20,
  sortBy = "ordersCreated",
  order: "asc" | "desc" = "desc"
): Promise<AnalyticsOperatorsResponse> {
  const params = new URLSearchParams(queryString(filters));
  params.set("page", String(page));
  params.set("limit", String(limit));
  params.set("sortBy", sortBy);
  params.set("order", order);
  const res = await fetch(`${API_BASE_URL}/api/analytics/operators?${params}`, {
    headers: authHeaders(),
  });
  if (!res.ok) throw new Error(await res.text());
  const json = await res.json();
  if (!json.success) throw new Error(json.error || "Failed to load operators");
  return json.data;
}

export async function fetchAnalyticsFollowups(
  filters: AnalyticsFilters
): Promise<AnalyticsFollowUpsResponse> {
  const qs = queryString(filters);
  const res = await fetch(`${API_BASE_URL}/api/analytics/followups?${qs}`, {
    headers: authHeaders(),
  });
  if (!res.ok) throw new Error(await res.text());
  const json = await res.json();
  if (!json.success) throw new Error(json.error || "Failed to load follow-ups");
  return json.data;
}

export async function fetchAnalyticsTasks(
  filters: AnalyticsFilters
): Promise<AnalyticsTasksResponse> {
  const qs = queryString(filters);
  const res = await fetch(`${API_BASE_URL}/api/analytics/tasks?${qs}`, {
    headers: authHeaders(),
  });
  if (!res.ok) throw new Error(await res.text());
  const json = await res.json();
  if (!json.success) throw new Error(json.error || "Failed to load tasks");
  return json.data;
}

export async function fetchAnalyticsQuality(): Promise<AnalyticsQualityResponse> {
  const res = await fetch(`${API_BASE_URL}/api/analytics/quality`, {
    headers: authHeaders(),
  });
  if (!res.ok) throw new Error(await res.text());
  const json = await res.json();
  if (!json.success) throw new Error(json.error || "Failed to load quality");
  return json.data;
}

export interface FilterOptionsData {
  tasks: string[];
  operators: { id: string; username: string }[];
}

export async function fetchAnalyticsFilterOptions(): Promise<FilterOptionsData> {
  const res = await fetch(`${API_BASE_URL}/api/analytics/filter-options`, {
    headers: authHeaders(),
  });
  if (!res.ok) throw new Error(await res.text());
  const json = await res.json();
  if (!json.success) throw new Error(json.error || "Failed to load filter options");
  return json.data;
}

export async function fetchAnalyticsRevenue(
  filters: AnalyticsFilters,
  currency?: string
): Promise<AnalyticsRevenueResponse> {
  const params = new URLSearchParams(queryString(filters));
  if (currency) params.set("currency", currency);
  const res = await fetch(`${API_BASE_URL}/api/analytics/revenue?${params}`, {
    headers: authHeaders(),
  });
  if (!res.ok) throw new Error(await res.text());
  const json = await res.json();
  if (!json.success) throw new Error(json.error || "Failed to load revenue");
  return json.data;
}

export async function fetchAnalyticsOrdersProducts(
  filters: AnalyticsFilters
): Promise<AnalyticsOrdersProductsResponse> {
  const qs = queryString(filters);
  const res = await fetch(`${API_BASE_URL}/api/analytics/orders-products?${qs}`, {
    headers: authHeaders(),
  });
  if (!res.ok) throw new Error(await res.text());
  const json = await res.json();
  if (!json.success) throw new Error(json.error || "Failed to load orders/products");
  return json.data;
}

export async function fetchAnalyticsPerformance(
  filters: AnalyticsFilters
): Promise<AnalyticsPerformanceResponse> {
  const qs = queryString(filters);
  const res = await fetch(`${API_BASE_URL}/api/analytics/performance?${qs}`, {
    headers: authHeaders(),
  });
  if (!res.ok) throw new Error(await res.text());
  const json = await res.json();
  if (!json.success) throw new Error(json.error || "Failed to load performance");
  return json.data;
}

export async function fetchAnalyticsCallbacksReasons(
  filters: AnalyticsFilters
): Promise<AnalyticsCallbacksReasonsResponse> {
  const qs = queryString(filters);
  const res = await fetch(`${API_BASE_URL}/api/analytics/callbacks-reasons?${qs}`, {
    headers: authHeaders(),
  });
  if (!res.ok) throw new Error(await res.text());
  const json = await res.json();
  if (!json.success) throw new Error(json.error || "Failed to load callback reasons");
  return json.data;
}

export async function fetchAnalyticsOutcomes(
  filters: AnalyticsFilters
): Promise<AnalyticsOutcomesResponse> {
  const qs = queryString(filters);
  const res = await fetch(`${API_BASE_URL}/api/analytics/outcomes?${qs}`, {
    headers: authHeaders(),
  });
  if (!res.ok) throw new Error(await res.text());
  const json = await res.json();
  if (!json.success) throw new Error(json.error || "Failed to load outcomes");
  return json.data;
}

export async function fetchAnalyticsShipping(
  filters: AnalyticsFilters
): Promise<AnalyticsShippingResponse> {
  const qs = queryString(filters);
  const res = await fetch(`${API_BASE_URL}/api/analytics/shipping?${qs}`, {
    headers: authHeaders(),
  });
  if (!res.ok) throw new Error(await res.text());
  const json = await res.json();
  if (!json.success) throw new Error(json.error || "Failed to load shipping stats");
  return json.data;
}
